import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { BookOpenText, CheckCircle2, Megaphone, RefreshCw, Search } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import {
  StoreAnnouncement,
  StorePaginationMeta,
  StoreUser,
  getCurrentStoreUser,
  getStoreAnnouncement,
  listStoreAnnouncements,
  logoutStore,
  markStoreAnnouncementRead,
} from "./api";
import Pagination from "../admin/components/Pagination";

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

export default function StoreAnnouncements() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [items, setItems] = useState<StoreAnnouncement[]>([]);
  const [selectedItem, setSelectedItem] = useState<StoreAnnouncement | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [categoryOptions, setCategoryOptions] = useState<{ Key: string; Label: string }[]>([]);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const effectiveLocationId = useMemo(() => {
    return preferredStoreLocationId(user, currentLocationId);
  }, [currentLocationId, user]);

  async function load(filters = { LocationId: effectiveLocationId, Category: category, Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    if (!filters.LocationId) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await listStoreAnnouncements(filters);
      setItems(data.Items || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      setCategoryOptions(data.CategoryOptions || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "公告載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await getCurrentStoreUser();
        if (!alive) return;
        setUser(me.User);
        setCurrentLocationId(preferredStoreLocationId(me.User, currentLocationId));
      } catch {
        navigate("/store-login", { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [currentLocationId, navigate]);

  useEffect(() => {
    if (!effectiveLocationId) return;
    load({ LocationId: effectiveLocationId, Category: category, Status: status, Keyword: keyword.trim(), Page: 1, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocationId]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (status) next.set("status", status);
    if (keyword.trim()) next.set("keyword", keyword.trim());
    setSearchParams(next, { replace: true });
  }, [keyword, setSearchParams, status]);

  useEffect(() => {
    if (!params.storeAnnouncementId || !effectiveLocationId) return;
    openDetail(params.storeAnnouncementId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.storeAnnouncementId, effectiveLocationId]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    setSelectedItem(null);
    navigate("/store/announcements");
    load({ LocationId: locationId, Category: category, Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function openDetail(id: string) {
    if (!effectiveLocationId) return;
    setDetailLoading(true);
    setMessage("");
    try {
      const data = await getStoreAnnouncement(id, effectiveLocationId);
      setSelectedItem(data.Item);
      if (params.storeAnnouncementId !== id) navigate(`/store/announcements/${id}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "讀取公告失敗。");
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleMarkRead() {
    if (!selectedItem || !effectiveLocationId) return;
    setSaving(true);
    setMessage("");
    try {
      const data = await markStoreAnnouncementRead(selectedItem.StoreAnnouncementId, effectiveLocationId);
      setSelectedItem(data.Item);
      setItems((current) => current.map((item) => item.StoreAnnouncementId === data.Item.StoreAnnouncementId ? data.Item : item));
      await load({ LocationId: effectiveLocationId, Category: category, Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "標記已讀失敗。");
    } finally {
      setSaving(false);
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ LocationId: effectiveLocationId, Category: category, Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="公告 SOP" user={user}>
      <form className="mb-4 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 lg:p-4 2xl:grid-cols-[170px_170px_minmax(300px,1fr)_auto] 2xl:items-end" onSubmit={handleSearch}>
        <Field label="類型">
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">全部</option>
            {categoryOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </Field>
        <Field label="閱讀狀態">
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Unread">未讀</option>
            <option value="Read">已讀</option>
          </select>
        </Field>
        <Field label="關鍵字" className="sm:col-span-2 2xl:col-span-1">
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal text-[#203d32] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="標題、公告內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Field>
        <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => load()} type="button"><RefreshCw size={18} />整理</button>
        </div>
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Summary label="全部" value={counts.Total || 0} />
        <Summary label="未讀" value={counts.Unread || 0} />
        <Summary label="已讀" value={counts.Read || 0} />
      </div>
      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(360px,.75fr)]">
        <section className="rounded-md border border-[#dfe6e1] bg-white">
          <div className="grid gap-3 p-3">
            {loading ? (
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f]">載入中...</div>
            ) : items.length === 0 ? (
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f]">目前沒有公告。</div>
            ) : items.map((item) => (
              <button
                className={`grid gap-3 rounded-md border p-4 text-left transition hover:bg-[#f7faf8] ${selectedItem?.StoreAnnouncementId === item.StoreAnnouncementId ? "border-[#9ac8aa] bg-[#f1f8f3]" : "border-[#edf0ee] bg-white"}`}
                key={item.StoreAnnouncementId}
                onClick={() => openDetail(item.StoreAnnouncementId)}
                type="button"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <Badge>{item.CategoryLabel}</Badge>
                      {item.Priority === "High" && <Badge tone="warn">重要</Badge>}
                      {item.IsPinned && <Badge tone="green">置頂</Badge>}
                      <Badge tone={item.IsRead ? "muted" : "green"}>{item.IsRead ? "已讀" : "未讀"}</Badge>
                    </div>
                    <h2 className="mt-3 break-words text-lg font-extrabold text-[#203d32]">{item.Title}</h2>
                  </div>
                  <BookOpenText className="shrink-0 text-[#315746]" size={22} />
                </div>
                <p className="line-clamp-2 whitespace-pre-line text-sm leading-6 text-[#59665f]">{item.Body}</p>
                <div className="text-xs font-bold text-[#7b877f]">發布：{item.PublishedAt || "-"}</div>
              </button>
            ))}
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ LocationId: effectiveLocationId, Category: category, Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
        </section>

        <aside className="rounded-md border border-[#dfe6e1] bg-white">
          {!selectedItem ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center text-[#59665f]">
              <Megaphone className="mb-3 text-[#9aa8a0]" size={34} />
              <p className="font-bold text-[#203d32]">選擇公告</p>
              <p className="mt-2 text-sm leading-6">這裡會顯示總部發布給目前門市的公告、SOP、活動話術與制度提醒。</p>
            </div>
          ) : (
            <div className="grid gap-4 p-4">
              <div className="border-b border-[#edf0ee] pb-4">
                <div className="flex flex-wrap gap-2">
                  <Badge>{selectedItem.CategoryLabel}</Badge>
                  {selectedItem.Priority === "High" && <Badge tone="warn">重要</Badge>}
                  {selectedItem.IsPinned && <Badge tone="green">置頂</Badge>}
                  <Badge tone={selectedItem.IsRead ? "muted" : "green"}>{selectedItem.IsRead ? "已讀" : "未讀"}</Badge>
                </div>
                <h2 className="mt-3 break-words text-2xl font-extrabold text-[#203d32]">{selectedItem.Title}</h2>
                <div className="mt-2 text-xs font-bold text-[#7b877f]">發布：{selectedItem.PublishedAt || "-"}　到期：{selectedItem.ExpiresAt || "無"}</div>
                {detailLoading && <p className="mt-2 text-sm text-[#59665f]">明細載入中...</p>}
              </div>
              <div className="whitespace-pre-wrap break-words text-base leading-8 text-[#34463c]">{selectedItem.Body}</div>
              {!selectedItem.IsRead && (
                <button className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60 sm:w-auto" disabled={saving} onClick={handleMarkRead} type="button">
                  <CheckCircle2 size={18} />{saving ? "處理中" : "標記已讀"}
                </button>
              )}
              {selectedItem.IsRead && <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] px-4 py-3 text-sm font-bold text-[#315746]">已讀時間：{selectedItem.ReadAt || "-"}</div>}
            </div>
          )}
        </aside>
      </div>
    </StoreLayout>
  );
}

function Field({ children, className = "", label }: { children: ReactNode; className?: string; label: string }) {
  return <label className={`grid min-w-0 gap-1 text-sm text-[#59665f] ${className}`}><span className="font-bold">{label}</span>{children}</label>;
}

function Summary({ label, value }: { label: string; value: number }) {
  return <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3"><p className="text-xs font-bold text-[#7b877f]">{label}</p><p className="mt-1 text-2xl font-bold text-[#203d32]">{value}</p></div>;
}

function Badge({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "green" | "warn" }) {
  const className = tone === "green" ? "bg-[#e9f4ec] text-[#245438]" : tone === "warn" ? "bg-[#fff7e8] text-[#9a5b16]" : "bg-[#eef3f8] text-[#255071]";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${className}`}>{children}</span>;
}
